/**
 * **One retrieval layer** (`docs/Retrieval foundation.md` §1, Phase 2): every read tool answers through *candidate
 * sources → fusion → hydration with provenance*. This file is the pure half: the types, the fusion and the budgets.
 * No I/O. The sources and the hydration live in `packages/db`, one module per table family; the tools in
 * `agent-read.ts` keep their grant checks, audit rows and answer framing.
 *
 * **A candidate is a reference, never a record.** A source proposes ids with a rank; hydration loads each one under
 * row-level security and builds its provenance from the row's own columns, so a record the grant does not reach
 * cannot come back, and a result cannot leave without its source.
 *
 * **Fusion is reciprocal rank fusion** (§3): `score(d) = Σ 1 / (k + rank_s(d))` over the sources that proposed `d`,
 * with `k = 60`. It needs no calibration between `ts_rank_cd` and a cosine, which have nothing in common, and one bad
 * source cannot outvote the rest. Ties break by the item's own date, newest first, then by id, so two runs give one
 * order.
 */

import { measuredOn, neverMeasured } from './measured.ts';

/** What kind of evidence proposed a record. `typed` and `live` hydrate only; nothing is ranked by similarity there. */
export type RetrievalSourceKind = 'text' | 'vector' | 'typed' | 'graph' | 'live';

/** What the grant reaches, resolved before any source runs. Every source takes it as predicates in its own query. */
export interface RetrievalScope {
  tenantId: string;
  /** Org: the legal entities this answer may read. */
  entityIds?: readonly string[];
  /** Me: the connected accounts this answer may read. */
  accountIds?: readonly string[];
  /** Me: mail, calendar, drive, contacts. */
  surfaces?: readonly string[];
  /** Bitemporal reads (beliefs, the ledger): as known at this instant. */
  asKnownAt?: Date;
}

export interface RetrievalBudget {
  /** The most candidates one source may propose. */
  rows: number;
  /** How long one source may take before the answer goes on without it. */
  ms: number;
}

export interface RetrievalRequest<F = Record<string, unknown>> {
  /** The tool asking: `me.search`, `org.documents`, … */
  tool: string;
  scope: RetrievalScope;
  /** The question's words. Absent for a purely typed tool. */
  query?: string;
  /** Tool-specific, typed per tool. */
  filters: F;
  limit: number;
  budget: RetrievalBudget;
}

/** A record, by table and id: what a source proposes and what hydration loads. */
export interface RecordRef {
  table: string;
  id: string;
}

export interface Candidate {
  ref: RecordRef;
  /** The source's name, as in `CandidateSource.name`: `me_item.words`, `me_item.meaning`, … */
  source: string;
  kind: RetrievalSourceKind;
  /** 1-based, within its source. */
  rank: number;
  /** Source-native and never compared across sources: `ts_rank_cd`, `1 − cosine distance`, a depth. */
  score?: number;
  /** The item's own date (ISO), for ties: the newer of two equals first. */
  itemAt?: string;
}

/** One record after fusion: every source that proposed it, its fused score and its place. */
export interface Fused {
  ref: RecordRef;
  /** Every source that proposed it, in the order the sources were given. */
  foundBy: string[];
  /** Every kind of evidence that proposed it, without repeats. */
  kinds: RetrievalSourceKind[];
  score: number;
  /** 1-based. */
  fusedRank: number;
  itemAt?: string;
}

/** The RRF constant. Cormack et al.'s 60, the value `org.documents` has used since F157. */
export const RRF_K = 60;

/**
 * **How many candidates one source may propose** (§3): four times the page, so fusion has room to reorder and a
 * source cannot flood it.
 */
export const CANDIDATES_PER_RESULT = 4;

/**
 * **How long the query embedding may take** (§3): past it, the answer is the words' alone, with a gap that says
 * meaning was unavailable. It never waits and never errors.
 */
export const EMBED_BUDGET_MS = neverMeasured(
  400,
  'how long a query embedding may take before the answer goes on with the words alone',
);

const key = (ref: RecordRef) => `${ref.table}\u0000${ref.id}`;

/** The budget a request gives each source: `limit × 4` rows, the embedding's time. */
export function budgetFor(limit: number, ms: number = EMBED_BUDGET_MS.value): RetrievalBudget {
  return { rows: Math.max(1, Math.trunc(limit)) * CANDIDATES_PER_RESULT, ms };
}

/**
 * Reciprocal rank fusion over the lists the sources returned, each already capped at its budget. A record proposed
 * twice by one source counts once, at its best rank: a source that repeats itself is not two pieces of evidence.
 */
export function fuse(lists: readonly (readonly Candidate[])[], limit: number, k = RRF_K): Fused[] {
  const byRef = new Map<string, Fused>();
  for (const list of lists) {
    const best = new Map<string, Candidate>();
    for (const c of list) {
      const seen = best.get(key(c.ref));
      if (seen === undefined || c.rank < seen.rank) best.set(key(c.ref), c);
    }
    for (const [id, c] of best) {
      const held = byRef.get(id) ?? {
        ref: c.ref,
        foundBy: [],
        kinds: [],
        score: 0,
        fusedRank: 0,
        ...(c.itemAt === undefined ? {} : { itemAt: c.itemAt }),
      };
      held.score += 1 / (k + c.rank);
      held.foundBy.push(c.source);
      if (!held.kinds.includes(c.kind)) held.kinds.push(c.kind);
      if (held.itemAt === undefined && c.itemAt !== undefined) held.itemAt = c.itemAt;
      byRef.set(id, held);
    }
  }
  return [...byRef.values()]
    .sort(
      (a, b) =>
        b.score - a.score ||
        (b.itemAt ?? '').localeCompare(a.itemAt ?? '') ||
        a.ref.id.localeCompare(b.ref.id),
    )
    .slice(0, Math.max(0, Math.trunc(limit)))
    .map((f, i) => ({ ...f, fusedRank: i + 1 }));
}

/** Ranks a source's rows 1, 2, 3 … in the order it returned them, and caps them at its budget. */
export function ranked<T>(
  rows: readonly T[],
  budget: RetrievalBudget,
  toCandidate: (row: T, rank: number) => Candidate,
): Candidate[] {
  return rows.slice(0, budget.rows).map((row, i) => toCandidate(row, i + 1));
}

/**
 * **How much further than the nearest a record may be and still be proposed by meaning** under hybrid ranking (§3),
 * in cosine distance. The absolute bound (F220) keeps a meaningless neighbour out; this one keeps a neighbour that is
 * merely *also near* from outranking the words, which is where a near-duplicate decoy came from: on the eval it sat
 * directly behind the answer, 0.34–0.37 against 0.26–0.35.
 */
export const MEANING_BEYOND_BEST = measuredOn(
  0.04,
  'the retrieval eval by meaning (gemini-embedding-001, 158 recorded vectors), 2026-10-28: with at most 5 by meaning, + 0.02 gave recall@5 78/99 and exclusion 23/59, + 0.04 gave 81/99 and 22/59, + 0.06 gave 84/99 and 20/59, no bound 84/99 and 14/59',
  'how much further than the nearest a record may be and still be proposed by meaning under hybrid ranking',
);

/**
 * The rows a vector source proposes: nearest first, as the source returned them, no further than `margin` beyond the
 * first, and at most `take`. Rows not in distance order are a source's error, and the bound is read from the first.
 */
export function nearBest<T extends { distance: number }>(
  rows: readonly T[],
  margin: number,
  take: number,
): T[] {
  const best = rows[0]?.distance;
  if (best === undefined) return [];
  return rows.filter((r) => r.distance <= best + margin).slice(0, Math.max(0, Math.trunc(take)));
}
